import React, { useState } from 'react';
import { Form, Input, Button, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import './Login.css';
import { BASE_URL } from '../conf/config';
import { getCookie, setCookie, deleteCookie } from '../conf/config';
import ChangePasswordModal, { postPassword } from '../components/ChangePasswordModal';
import { useInstance } from '../useInstance';

// LoginPage component with login form
const LoginPage = ({ setIsAuthenticated }) => {
    // State for loading indicator and error message
    const [isLoggingIn, setIsLoggingIn] = useState(false); // Controls loading state for login button
    const [errorMessage, setErrorMessage] = useState(''); // Stores any error messages for display
    const [forgot, setForgot] = useState(false); // "Forgot password?" form instead of the login form
    const [forgotReply, setForgotReply] = useState('');
    const [expired, setExpired] = useState(null); // {username, password} while the expired-password dialog is open
    const instance = useInstance(); // demo data: say so before anyone logs in

    // Async function triggered upon form submission
    const onFinish = async (values) => {
        setIsLoggingIn(true); // Set loading state to true, showing button spinner
        setErrorMessage('');

        try {
            // Send a POST request to the backend login endpoint
            const response = await fetch(`${BASE_URL}/api/token/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json', // Specify JSON format for the request
                },
                body: JSON.stringify({
                    username: values.username, // Username from form input
                    password: values.password, // Password from form input
                }),
            });

            const data = await response.json(); // Parse the response data

            if (response.ok) {
                // Login successful, retrieve and store access and refresh tokens
                const { access, refresh } = data;
                setCookie('access_token', access, 1); // Save access token in cookies
                setCookie('refresh_token', refresh, 24); // Save refresh token in cookies
                // App warns once after login when the password expires soon
                try { sessionStorage.setItem('password_expires_in_days', String(data.password_expires_in_days)); } catch (e) { /* not essential */ }
                setIsAuthenticated(true); // Update authentication state in parent component
                window.location.href = '/'; // Redirect to homepage
            } else if (data.code === 'password_expired') {
                // Right credentials, but the password has to be replaced first
                setExpired({ username: values.username, password: values.password });
                setIsLoggingIn(false);
            } else {
                // If login failed, throw an error to display the message
                throw new Error(data.detail || 'Login failed');
            }
        } catch (error) {
            // Catch errors and set error message for display
            console.error('Login error:', error);
            setErrorMessage('Login failed. Please check your credentials and try again.');
            setIsLoggingIn(false); // Reset loading state
        }
    };

    const onForgot = async (values) => {
        setIsLoggingIn(true);
        const result = await postPassword('reset/', { login: values.login });
        setIsLoggingIn(false);
        setForgotReply(result.ok ? result.detail : result.errors.join(' '));
    };

    if (forgot) {
        return (
            <div className="login-container">
                <Form name="forgot_password" className="login-form" onFinish={onForgot}>
                    <h2 style={{ marginTop: 0 }}>Forgot password?</h2>
                    {forgotReply ? (
                        <div style={{ marginBottom: 24 }}>{forgotReply}</div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 16, fontSize: 15 }}>
                                Enter your username or email address. We'll mail you a link to set a new password.
                            </div>
                            <Form.Item name="login" rules={[{ required: true, message: 'Please input your username or email!' }]}>
                                <Input
                                    className='login-input'
                                    prefix={<UserOutlined className="site-form-item-icon" />}
                                    placeholder="Username or email"
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" className="login-form-button" loading={isLoggingIn}>
                                    Send link
                                </Button>
                            </Form.Item>
                        </>
                    )}
                    <Button type="link" style={{ padding: 0 }} onClick={() => { setForgot(false); setForgotReply(''); }}>
                        Back to login
                    </Button>
                </Form>
            </div>
        );
    }

    return (
        <div className="login-container">
            <Form
                name="normal_login"
                className="login-form"
                initialValues={{ remember: true }}
                onFinish={onFinish} // Trigger onFinish function on form submission
            >
                {instance.demo && (
                    <div style={{ marginBottom: 20, padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, fontSize: 14, lineHeight: 1.5 }}>
                        <b style={{ color: '#cf1322' }}>Demo data.</b> Every customer, person and number here is made up.
                        Log in as {instance.logins?.join(' or ')}.
                    </div>
                )}

                {/* Username input field */}
                <Form.Item
                    name="username"
                    rules={[{ required: true, message: 'Please input your Username!' }]}
                >
                    <Input
                        className='login-input'
                        prefix={<UserOutlined className="site-form-item-icon" />}
                        placeholder="Username"
                    />
                </Form.Item>

                {/* Password input field */}
                <Form.Item
                    name="password"
                    rules={[{ required: true, message: 'Please input your Password!' }]}
                >
                    <Input
                        className='login-input'
                        prefix={<LockOutlined className="site-form-item-icon" />}
                        type="password"
                        placeholder="Password"
                    />
                </Form.Item>

                {/* Display error message if present */}
                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

                {/* Login button, triggers form submission when clicked */}
                <Form.Item>
                    <Button type="primary" htmlType="submit" className="login-form-button" loading={isLoggingIn}>
                        Log in
                    </Button>
                </Form.Item>

                <Button type="link" style={{ padding: 0 }} onClick={() => setForgot(true)}>
                    Forgot password?
                </Button>
            </Form>

            {/* Expired password: set a new one, then log in with it */}
            <ChangePasswordModal
                open={!!expired}
                username={expired?.username}
                currentPassword={expired?.password}
                title="Your password has expired"
                intro="Please choose a new password to continue."
                onCancel={() => setExpired(null)}
                onChanged={(newPassword) => {
                    const { username } = expired;
                    setExpired(null);
                    onFinish({ username, password: newPassword });
                }}
            />
        </div>
    );
};

export default LoginPage;
